import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMarquees } from '../../hooks/useMarquees';
import { NotificationCenter } from './NotificationCenter/NotificationCenter';
import './GlobalHeader.css';
import SearchInput from './SearchInput';
import { UserDropdown } from './UserDropdown';
import MarqueeBanner from './MarqueeBanner';
import type { Notification } from '../../services/notificationsApi';

interface GlobalHeaderProps {
  showMarquee?: boolean;
}

/**
 * Check if user has admin privileges (moderator, admin, or super_admin)
 */
const hasAdminPrivileges = (user: { role: string } | null): boolean => {
  if (!user) return false;
  return ['moderator', 'admin', 'super_admin'].includes(user.role);
};

/**
 * 全域頂部導覽列
 * 對應 UXC-common-GlobalHeader-001
 */
const GlobalHeader = ({ showMarquee = true }: GlobalHeaderProps) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const { marquees, enabled: marqueeEnabled, loading: marqueeLoading, settings: marqueeSettings } = useMarquees();

  // 搜尋處理 - 直接導航到廣告列表頁面
  const handleSearch = useCallback((query: string) => {
    if (query.trim()) {
      navigate(`/ads?keyword=${encodeURIComponent(query.trim())}`);
    } else {
      navigate('/ads');
    }
  }, [navigate]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  // Handle notification click - navigate to relevant page
  const handleNotificationClick = (notification: Notification) => {
    // Notifications can have links in their data
    const link = (notification.data as any)?.link;
    if (link) {
      navigate(link);
    }
  };

  // Show marquee only when prop AND global setting are both true
  const shouldShowMarquee = showMarquee && marqueeEnabled && !marqueeLoading;

  const headerClass = `global-header${shouldShowMarquee ? ' global-header--with-marquee' : ''}`;

  return (
    <>
      {/* Marquee Banner - rendered when both prop and global setting are enabled */}
      {shouldShowMarquee && (
        <MarqueeBanner
          marquees={marquees}
          enabled={true}
          settings={marqueeSettings}
        />
      )}
      <header className={headerClass}>
        <div className="global-header__container">
        {/* Logo */}
        <div className="global-header__logo">
          <a href="/">
            <h1>瑋瑋車業</h1>
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="global-header__nav">
          {/* Search - moved after logo */}
          <div className="global-header__search">
            <SearchInput
              placeholder="搜尋廣告..."
              onSearch={handleSearch}
            />
          </div>

          {/* Main Navigation */}
          <a href="/ads" className="global-header__nav-link">
            廣告列表
          </a>
          <a href="/wishes" className="global-header__nav-link">
            許願池
          </a>

          {/* User Dropdown - replaces member/admin links */}
          {isAuthenticated ? (
            <>
              {/* Notification Center */}
              <NotificationCenter onNotificationClick={handleNotificationClick} />
              <UserDropdown />
            </>
          ) : (
            !isLoading && (
              <a href="/login" className="global-header__nav-link">
                登入
              </a>
            )
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="global-header__mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="選單"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="global-header__mobile-menu">
          <a href="/ads" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            廣告列表
          </a>
          <a href="/wishes" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
            許願池
          </a>
          {isAuthenticated ? (
            <>
              <div className="global-header__mobile-user-info">
                {user?.name}
              </div>
              <a href="/member" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                會員中心
              </a>
              <a href="/help" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                操作手冊
              </a>
              {hasAdminPrivileges(user) && (
                <>
                  <div className="global-header__mobile-divider"></div>
                  <a href="/admin" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                    管理後台
                  </a>
                  <a href="/admin/queue" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                    待審廣告
                  </a>
                  <a href="/admin/marquees" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                    跑馬燈管理
                  </a>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <a href="/admin/members" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                      會員管理
                    </a>
                  )}
                  {user?.role === 'super_admin' && (
                    <a href="/admin/system/bootstrap" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                      Bootstrap 工具
                    </a>
                  )}
                </>
              )}
              <button
                onClick={handleLogout}
                className="global-header__mobile-logout-btn"
              >
                登出
              </button>
            </>
          ) : (
            !isLoading && (
              <>
                <a href="/login" className="global-header__mobile-link" onClick={() => setMobileMenuOpen(false)}>
                  登入
                </a>
              </>
            )
          )}
        </div>
      )}
    </header>
    </>
  );
};

export default GlobalHeader;
